import { getTotalPrice } from '@shared/lib/money';
import { useMemo, useReducer } from 'react';
import { parse } from 'date-fns';

import {
	SegmentFragment,
	PassengerFragment,
	BaggageFragment,
	CheckinPassengerFragment,
	InputGdsServiceParameter,
	GdAdditionalParameterFragment
} from '@websky/graphql';
import { MoneyType } from '../../../PriceMatrix/types';
import { API_DATE_FORMAT, format } from '../../../utils';

interface ChangeActionPayload {
	segmentId: string;
	segmentIds: string[];
	travellerId: string;
	serviceId?: string;
}

interface ChangeAction {
	type: 'change';
	payload: ChangeActionPayload;
}

interface ClearAction {
	type: 'clear';
}

type ActionTypes = ChangeAction | ClearAction;

interface State {
	[segmentId: string]: {
		[travellerId: string]: string; // serviceId
	};
}

export interface SegmentService {
	service: BaggageFragment;
	price: MoneyType;
	serviceId: string;
	segmentId: string;
	segmentIds: string[];
	date: string;
	additionalFields: GdAdditionalParameterFragment[];
}

export interface SelectedService {
	service: BaggageFragment;
	serviceId: string;
	segmentId: string;
	segmentIds: string[];
	passengerId: string;
	count: number;
	extraValues: InputGdsServiceParameter[];
}

const reducer = (state: State, action: ActionTypes) => {
	switch (action.type) {
		case 'change':
			const changeState = { ...state };
			action.payload.segmentIds.forEach(segmentId => {
				changeState[segmentId] = {
					...changeState[segmentId],
					[action.payload.travellerId]: action.payload.serviceId
				};
			});

			return changeState;
		case 'clear':
			const clearState = { ...state };
			for (const segmentId in clearState) {
				for (const passengerId in clearState[segmentId]) {
					clearState[segmentId][passengerId] = null;
				}
			}

			return clearState;
		default:
			return state;
	}
};

export const useAnimalState = (
	services: BaggageFragment[],
	segments: SegmentFragment[],
	travellers: (PassengerFragment | CheckinPassengerFragment)[]
) => {
	const initialState = useMemo(() => {
		const state: State = {};

		segments.forEach(segment => {
			const segmentId = segment.id;

			services.forEach(service => {
				const allowedSegmentsSet = new Set(service.allowedSegments.reduce((acc, ids) => [...acc, ...ids], []));

				if (allowedSegmentsSet.has(segmentId)) {
					travellers.forEach(traveller => {
						const selectedService = traveller.services?.gdsServices?.services?.find(
							selectedService =>
								selectedService.serviceId === service.id &&
								selectedService.segmentIds.includes(segmentId)
						);

						state[segmentId] = {
							...state[segmentId],
							[traveller.id]: selectedService?.serviceId ?? null
						};
					});
				}
			});
		});

		return state;
	}, [services, segments, travellers]);

	const [state, dispatch] = useReducer(reducer, initialState);

	const servicesBySegment = useMemo(() => {
		const servicesMap = new Map<string, SegmentService>();

		segments.forEach(segment => {
			const segmentId = segment.id;

			services.forEach(service => {
				const allowedSegmentMap = new Map(service.allowedSegments.map(ids => [ids[0], ids]));

				if (allowedSegmentMap.has(segmentId)) {
					const allowedSegments = allowedSegmentMap.get(segmentId);

					let direction = `${segment.departure.airport.city.name} – ${segment.arrival.airport.city.name}`;
					let date = format(parse(segment.departure.date, API_DATE_FORMAT, new Date()), 'dd MMM');

					if (allowedSegments.length > 1) {
						const firstSegmentId = allowedSegments[0];
						const lastSegmentId = allowedSegments[allowedSegments.length - 1];

						const firstSegment = segments.find(segment => segment.id === firstSegmentId);
						const lastSegment = segments.find(segment => segment.id === lastSegmentId);

						direction = `${firstSegment.departure.airport.city.name} – ${lastSegment.arrival.airport.city.name}`;
						date = format(parse(firstSegment.departure.date, API_DATE_FORMAT, new Date()), 'dd MMM');
					}

					servicesMap.set(direction, {
						service,
						segmentId,
						date,
						segmentIds: allowedSegments,
						serviceId: service.id,
						price: service.price,
						additionalFields: service.requiredAdditionalInfo
					});
				}
			});
		});

		return [...servicesMap.entries()];
	}, [services, segments, travellers]);

	const [selectedServices, servicesToSave] = useMemo(() => {
		let selectedServices: SelectedService[] = [];
		let servicesToSave: SelectedService[] = [];

		for (const segmentId in state) {
			for (const passengerId in state[segmentId]) {
				const serviceId = state[segmentId][passengerId];
				const initialServiceId = initialState[segmentId][passengerId];
				const actualServiceId = serviceId ?? initialServiceId;

				const service = servicesBySegment.find(
					([_, service]) => service.segmentId === segmentId && service.serviceId === actualServiceId
				)?.[1];

				if (service) {
					const selectedService: SelectedService = {
						serviceId: actualServiceId,
						segmentId,
						passengerId,
						service: service.service,
						segmentIds: service.segmentIds,
						count: !!serviceId ? 1 : 0,
						extraValues: []
					};

					if (serviceId !== initialServiceId) {
						servicesToSave = [...servicesToSave, selectedService];
					} else if (serviceId) {
						selectedServices = [...selectedServices, selectedService];
					}
				}
			}
		}

		return [selectedServices, servicesToSave];
	}, [initialState, state, segments, servicesBySegment]);

	const hasChanges = useMemo(() => JSON.stringify(initialState) !== JSON.stringify(state), [initialState, state]);

	const totalPrice: MoneyType = useMemo(() => {
		const filterServices = [...selectedServices, ...servicesToSave].filter(service => service.count > 0);

		if (filterServices.length) {
			return getTotalPrice(filterServices, service => ({
				...service.service.price,
				amount: service.service.price.amount * service.segmentIds.length
			}));
		}

		return null;
	}, [selectedServices, servicesToSave]);

	const onChange = (payload: ChangeActionPayload) => {
		dispatch({ type: 'change', payload });
	};

	const onClear = () => {
		dispatch({ type: 'clear' });
	};

	return {
		state,
		servicesBySegment,
		selectedServices,
		initialState,
		servicesToSave,
		hasChanges,
		totalPrice,
		onChange,
		onClear
	};
};
